import { faEdit, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast from 'react-hot-toast';
import { useState } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  GetAdminProjectResponse,
  LicenseType,
} from 'src/project/types/projects';
import { ActionIcon } from 'src/common/components/buttons/ActionIcon';
import { deleteProject } from 'src/admin/apiProjects';
import { ProjectEditModal } from './ProjectEditModal';
import { ConfirmationDialog } from 'src/common/components/ConfirmationDialog';
import { Modal } from 'src/common/components/Modal';
import { Spinner } from 'src/common/components/Spinner';
import { Badge } from 'src/common/components/Badge';
import { getProjectFiles } from 'src/file/api';
import { SitelifeFile } from 'src/file/types';
import { calculateProjectFileType } from 'src/admin/utils/fileTypeUtils';

type Props = {
  project: GetAdminProjectResponse;
  preloadedFiles?: SitelifeFile[];
};

export const ProjectRow = ({ project, preloadedFiles }: Props) => {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const location = useLocation();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const queryClient = useQueryClient();

  // Detect if we're in admin context to optimize API calls
  const isInAdminContext = location.pathname.startsWith('/admin');

  // Determine project status based on license type and expiration date
  const determineStatus = () => {
    const licenseType = project.licenseType || project.license?.type;
    const expirationDate = project.license?.expirationDate;

    if (!expirationDate) return 'ACTIVE';

    if (licenseType === LicenseType.FREE) {
      return dayjs(expirationDate).isBefore(dayjs()) ? 'ARCHIVED' : 'ACTIVE';
    } else if (licenseType === LicenseType.BASIC) {
      return dayjs(expirationDate)
        .startOf('day')
        .isBefore(dayjs().startOf('day'))
        ? 'ARCHIVED'
        : 'ACTIVE';
    }

    return 'ACTIVE'; // Default for DEMO and other license types
  };

  const projectStatus = determineStatus();
  const statusColor = projectStatus === 'ACTIVE' ? 'green' : 'red';

  // Only fetch individual files if we don't have preloaded files in admin context
  // For non-admin contexts, always fetch to maintain existing behavior
  const shouldFetchFiles = !isInAdminContext || !preloadedFiles;

  // Use conditional query - only enabled when we need to fetch individual files
  const { data: fetchedFiles } = useQuery({
    queryKey: ['projects', project._id, 'files'],
    queryFn: () => getProjectFiles(project._id),
    enabled: shouldFetchFiles,
  });

  // Use preloaded files if available, otherwise fall back to fetched files
  const projectFiles = preloadedFiles || fetchedFiles;

  const determineFileTypesUsedByProject = () => {
    // Use the shared utility function for consistency
    const fileType = calculateProjectFileType(project, projectFiles);

    // Convert the result to the display format expected by the UI
    switch (fileType) {
      case '2D/3D':
        return '2D/3D';
      case '2D':
        return '2D';
      case '3D':
        return '3D';
      case 'empty':
        return '';
      case 'N/A':
        return 'N/A';
      default:
        return '';
    }
  };

  const determineLicenseType = () => {
    let license_type: string = project.license?.type;

    try {
      if (project.license?.type === LicenseType.BASIC) {
        const projectCreatedAt = project.createdAt
          ? dayjs(project.createdAt)
          : dayjs();
        const projectLicenseExpirationDate = project.license?.expirationDate
          ? dayjs(project.license.expirationDate)
          : dayjs();
        const licenseUpdatedAt = project.license?.licenseUpdatedAt
          ? dayjs(project.license.licenseUpdatedAt)
          : projectCreatedAt;
        const daysDifference = projectLicenseExpirationDate.diff(
          licenseUpdatedAt,
          'day',
        );

        if (licenseUpdatedAt.diff(project.createdAt, 'day') <= 30) {
          if (daysDifference <= 3) {
            license_type = 'Basic3';
            return license_type;
          } else if (daysDifference <= 30) {
            license_type = 'Basic30';
            return license_type;
          }
        } else {
          license_type = 'Basic';
          return license_type;
        }
      }
    } catch (error) {
      // console.error('Error determining license type:', error);
    }
    return license_type;
  };

  const handleConfirmDeleteProject = async () => {
    try {
      setIsLoading(true);
      await deleteProject(project._id);
      await queryClient.invalidateQueries({
        queryKey: ['admin-projects'],
      });
      toast.success(t('admin:projects.delete.success'));
    } catch (exception) {
      toast.error(t('admin:projects.delete.failure'));
    } finally {
      setIsLoading(false);
    }
    setIsDeleteConfirmationOpen(false);
  };

  return (
    <tr>
      <td className="table-td table-td-truncate min-w-[200px]">
        <div className="flex items-center">
          <div className="ml-4 overflow-hidden py-1">
            <div className="truncate text-base font-semibold">
              {project.name}
            </div>
          </div>
        </div>
      </td>
      <td className="table-td table-td-truncate lg:table-cell">
        {project.company && project.company.name}
      </td>
      <td className="table-td table-td-truncate lg:table-cell">
        {project.owner && project.owner.name}
      </td>
      <td className="table-td table-td-truncate lg:table-cell">
        {determineLicenseType()}
      </td>
      <td className="table-td table-td-truncate lg:table-cell">
        {determineFileTypesUsedByProject()}
      </td>
      <td className="table-td lg:table-cell">
        <Badge variant={statusColor} className="font-medium">
          {projectStatus}
        </Badge>
      </td>
      <td className="table-td  lg:table-cell">
        {project.createdAt &&
          (() => {
            const date = new Date(project.createdAt);
            const locale = i18n.language.startsWith('de') ? 'de-DE' : 'en-US';
            return `${date.getDate()}-${date.toLocaleString(locale, {
              month: 'short',
            })}-${date.getFullYear()}`;
          })()}
      </td>
      <td className="table-td lg:table-cell">
        {(() => {
          const licenseType = project.licenseType || project.license?.type;
          if (
            licenseType === LicenseType.BASIC &&
            project.license?.expirationDate
          ) {
            const date = new Date(project.license.expirationDate);
            const locale = i18n.language.startsWith('de') ? 'de-DE' : 'en-US';
            return `${date.getDate()}-${date.toLocaleString(locale, {
              month: 'short',
            })}-${date.getFullYear()}`;
          } else {
            const defaultDate = new Date(2099, 0, 1); // Jan 1, 2099
            const locale = i18n.language.startsWith('de') ? 'de-DE' : 'en-US';
            return `${defaultDate.getDate()}-${defaultDate.toLocaleString(
              locale,
              { month: 'short' },
            )}-${defaultDate.getFullYear()}`;
          }
        })()}
      </td>
      <td className="table-td flex">
        <ActionIcon
          className="ml-auto"
          onClick={() => setIsEditModalOpen(true)}
        >
          <FontAwesomeIcon icon={faEdit} />
        </ActionIcon>
        <ActionIcon onClick={() => setIsDeleteConfirmationOpen(true)}>
          <FontAwesomeIcon icon={faTrash} />
        </ActionIcon>
      </td>
      <ProjectEditModal
        project={project}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />
      <Modal
        isOpen={isDeleteConfirmationOpen}
        onRequestClose={() => setIsDeleteConfirmationOpen(false)}
      >
        {isLoading ? (
          <Spinner />
        ) : (
          <ConfirmationDialog
            title={t('admin:projects.delete.title')}
            message={t('admin:projects.delete.message')}
            onCancel={() => setIsDeleteConfirmationOpen(false)}
            onConfirm={handleConfirmDeleteProject}
            customConfirmText={t('common:button.delete')}
          />
        )}
      </Modal>
    </tr>
  );
};
